import React from 'react';

/**
 * ResponsiveImage — lightweight wrapper around <img> that builds an Unsplash
 * srcSet when the URL is from images.unsplash.com.
 *
 * Falls back to a plain <img> for non-Unsplash URLs (e.g. local /images/*).
 *
 * Props:
 *   - src: base image URL
 *   - alt: required alt text
 *   - widths: array of px widths to request (default: [480, 800, 1200])
 *   - sizes: standard <img sizes> attribute (default: '(max-width: 768px) 100vw, 50vw')
 *   - quality: Unsplash quality 1-100 (default: 80)
 *   - loading: 'lazy' (default) | 'eager'
 *   - className, style: forwarded to <img>
 */
const UNSPLASH_HOST = 'images.unsplash.com';

const isUnsplash = (url) => typeof url === 'string' && url.includes(UNSPLASH_HOST);

const buildUnsplashSrc = (baseUrl, width, quality) => {
  try {
    const u = new URL(baseUrl);
    u.searchParams.set('w', String(width));
    u.searchParams.set('q', String(quality));
    u.searchParams.set('auto', 'format');
    u.searchParams.set('fit', 'crop');
    return u.toString();
  } catch (err) {
    return baseUrl;
  }
};

const ResponsiveImage = ({
  src,
  alt,
  widths = [480, 800, 1200],
  sizes = '(max-width: 768px) 100vw, 50vw',
  quality = 80,
  loading = 'lazy',
  className,
  style,
  ...rest
}) => {
  if (!src) return null;
  if (!isUnsplash(src)) {
    return (
      <img
        src={src}
        alt={alt}
        loading={loading}
        decoding="async"
        className={className}
        style={style}
        {...rest}
      />
    );
  }

  const srcSet = widths
    .map((w) => `${buildUnsplashSrc(src, w, quality)} ${w}w`)
    .join(', ');
  const fallback = buildUnsplashSrc(src, widths[widths.length - 1], quality);

  return (
    <img
      src={fallback}
      srcSet={srcSet}
      sizes={sizes}
      alt={alt}
      loading={loading}
      decoding="async"
      className={className}
      style={style}
      {...rest}
    />
  );
};

export default ResponsiveImage;
